import React, { useEffect, useContext, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AppSettings } from "../../config/app-settings.js";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { login } from "../../app/auth/authSlice.js";
import axiosInstance, { clearForceLogoutState } from "../../config/axiosApi/axiosApi.jsx";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import userDataService from "../../services/userDataService.js";
import { areCookiesSupported } from "../../utils/cookieUtils.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import "./login.css";

const EMPTY_ERRORS = { userName: "", password: "" };

function Login() {
  const context = useContext(AppSettings);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [keepMeLogin, setKeepMeLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [showResetHelp, setShowResetHelp] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const userNameRef = useRef(null);
  const passwordRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const themeStyle = {
    "--bo-accent": themeColor,
    "--bo-accent-soft": `${themeColor}24`,
    "--bo-accent-shadow": `${themeColor}59`,
  };

  // Load keepMeLogin preference from localStorage on component mount
  useEffect(() => {
    // Session expiry can leave an in-memory block that rejects login without a network call.
    clearForceLogoutState();

    const savedKeepMeLogin = localStorage.getItem("keepMeLogin");
    if (savedKeepMeLogin !== null) {
      setKeepMeLogin(JSON.parse(savedKeepMeLogin));
    }
  }, []);

  const [activeBg, setActiveBg] = useState(
    "/assets/img/login-bg/login-bg-17.jpg"
  );
  const [bg1, setBg1] = useState(true);
  const [bg2, setBg2] = useState(false);
  const [bg3, setBg3] = useState(false);
  const [bg4, setBg4] = useState(false);
  const [bg5, setBg5] = useState(false);
  const [bg6, setBg6] = useState(false);
  const selectBg = (e, active, bg) => {
    e.preventDefault();

    setActiveBg(bg);
    setBg1(active === "bg1");
    setBg2(active === "bg2");
    setBg3(active === "bg3");
    setBg4(active === "bg4");
    setBg5(active === "bg5");
    setBg6(active === "bg6");
  };

  const validateForm = () => {
    const nextErrors = { ...EMPTY_ERRORS };

    if (!userName.trim()) {
      nextErrors.userName = "Email is required";
    }
    if (!password.trim()) {
      nextErrors.password = "Password is required";
    }

    setErrors(nextErrors);

    if (nextErrors.userName) {
      userNameRef.current?.focus();
      return false;
    }
    if (nextErrors.password) {
      passwordRef.current?.focus();
      return false;
    }

    return true;
  };

  const handleCapsLock = (event) => {
    if (typeof event.getModifierState === "function") {
      setCapsLockOn(event.getModifierState("CapsLock"));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const cookiesAllowed = navigator.cookieEnabled && areCookiesSupported();
    if (!cookiesAllowed) {
      const shouldOpenHelp = window.confirm(
        "Cookies are blocked in this browser. BackOffice login requires cookies for secure session. Click OK to see how to enable cookies."
      );

      if (shouldOpenHelp) {
        window.open("https://support.google.com/chrome/answer/95647", "_blank", "noopener,noreferrer");
      }

      showErrorToast("Please allow cookies to continue.");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(APIs.LOGIN, {
        userName,
        password,
        keepMeLogin,
        clientType: "web"
      });
      if (response.data.status || response.data.Status) {
        const payload = response.data.data || response.data.Data || {};
        const staffProfile = payload.userData || payload.UserData || null;
        const userData = {
          userData: staffProfile,
          keepMeLogin
        };

        dispatch(login(userData));
        localStorage.setItem("keepMeLogin", JSON.stringify(keepMeLogin));

        try {
          const userDataResponse = await userDataService.fetchUserData({
            allowPartial: true,
            silent: true,
            skipUnauthorizedRedirect: true
          });
          dispatch(login({
            ...userData,
            profileData: userDataResponse.profileData,
            settingsData: userDataResponse.settingsData,
            lastDataRefresh: userDataResponse.lastDataRefresh
          }));
        } catch (error) {
          // Profile/settings are not required to complete sign-in.
        }

        showSuccessToast('Logged in Successfully.');
        navigate("/dashboard");
      } else {
        showErrorToast(response.data.message);
      }
    } catch (err) {
      showErrorToast(err?.response?.data?.message ?? "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Immediately hide sidebar and header to prevent flash
    context.handleSetAppSidebarNone(true);
    context.handleSetAppHeaderNone(true);
    context.handleSetAppContentClass("p-0");
    return function cleanUp() {
      context.handleSetAppSidebarNone(false);
      context.handleSetAppHeaderNone(false);
      context.handleSetAppContentClass("");
    };
  }, [context]); // Add context as dependency to ensure it runs immediately

  // Additional effect to ensure immediate hiding
  useEffect(() => {
    // Force immediate hiding
    setTimeout(() => {
      context.handleSetAppSidebarNone(true);
      context.handleSetAppHeaderNone(true);
      context.handleSetAppContentClass("p-0");
    }, 0);
  }, []); // Run only once on mount

  const renderLogo = (variant) => {
    if (logoFailed) {
      return (
        <span className={`bo-login__wordmark bo-login__wordmark--${variant}`}>
          Serve<span>IQ</span>
        </span>
      );
    }

    return (
      <img
        src="/assets/img/login-logo.png"
        alt="ServeIQ"
        className={`bo-login__logo bo-login__logo--${variant}`}
        onError={() => setLogoFailed(true)}
      />
    );
  };

  return (
    <div
      className={`bo-login${isDarkMode ? " bo-login--dark" : ""}`}
      style={themeStyle}
    >
      <aside className="bo-login__brand">
        <div
          className="bo-login__brand-bg"
          style={{ backgroundImage: `url(${activeBg})` }}
          aria-hidden
        ></div>
        <div className="bo-login__brand-grid" aria-hidden></div>
        <div className="bo-login__brand-glow bo-login__brand-glow--one" aria-hidden></div>
        <div className="bo-login__brand-glow bo-login__brand-glow--two" aria-hidden></div>

        <div className="bo-login__brand-inner">
          <div>{renderLogo("full")}</div>

          <div className="bo-login__brand-copy">
            <h1>One BackOffice. Everything in sync.</h1>
            <p>Manage operations, teams and reporting from a single place.</p>
          </div>

          <p className="bo-login__brand-foot">
            &copy; {new Date().getFullYear()} ServeIQ
          </p>
        </div>
      </aside>

      <main className="bo-login__panel">
        <div className="bo-login__card">
          <div className="bo-login__card-brand">{renderLogo("compact")}</div>

          <h1 className="bo-login__title">welcome</h1>
          <p className="bo-login__subtitle">
            Sign in to access your BackOffice.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="bo-login__field">
              <label className="bo-login__label" htmlFor="userName">
                Email
              </label>
              <div
                className={`bo-login__input-wrap${errors.userName ? " is-invalid" : ""}`}
              >
                <i className="fas fa-envelope bo-login__input-icon" aria-hidden></i>
                <input
                  id="userName"
                  ref={userNameRef}
                  type="text"
                  className="bo-login__input"
                  value={userName}
                  placeholder="you@company.com"
                  autoComplete="username"
                  disabled={loading}
                  aria-invalid={Boolean(errors.userName)}
                  aria-describedby={errors.userName ? "userName-error" : undefined}
                  onChange={(e) => {
                    setUserName(e.target.value);
                    if (errors.userName) setErrors((prev) => ({ ...prev, userName: "" }));
                  }}
                />
              </div>
              {errors.userName && (
                <p className="bo-login__error" id="userName-error">
                  <i className="fas fa-circle-exclamation" aria-hidden></i>
                  {errors.userName}
                </p>
              )}
            </div>

            <div className="bo-login__field">
              <label className="bo-login__label" htmlFor="password">
                Password
              </label>
              <div
                className={`bo-login__input-wrap${errors.password ? " is-invalid" : ""}`}
              >
                <i className="fas fa-lock bo-login__input-icon" aria-hidden></i>
                <input
                  id="password"
                  ref={passwordRef}
                  type={showPassword ? "text" : "password"}
                  className="bo-login__input bo-login__input--with-action"
                  value={password}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={loading}
                  aria-invalid={Boolean(errors.password)}
                  aria-describedby={errors.password ? "password-error" : undefined}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors((prev) => ({ ...prev, password: "" }));
                  }}
                  onKeyUp={handleCapsLock}
                  onKeyDown={handleCapsLock}
                  onBlur={() => setCapsLockOn(false)}
                />
                <button
                  type="button"
                  className="bo-login__reveal"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i
                    className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                    aria-hidden
                  ></i>
                </button>
              </div>
              {errors.password && (
                <p className="bo-login__error" id="password-error">
                  <i className="fas fa-circle-exclamation" aria-hidden></i>
                  {errors.password}
                </p>
              )}
              {capsLockOn && !errors.password && (
                <p className="bo-login__hint bo-login__hint--warn" role="status">
                  <i className="fas fa-arrow-up" aria-hidden></i>
                  Caps Lock is on
                </p>
              )}
            </div>

            <div className="bo-login__row">
              <label className="bo-login__check" htmlFor="rememberMe">
                <input
                  id="rememberMe"
                  type="checkbox"
                  checked={keepMeLogin}
                  disabled={loading}
                  onChange={(e) => setKeepMeLogin(e.target.checked)}
                />
                <span>Keep me signed in</span>
              </label>
              <button
                type="button"
                className="bo-login__link"
                onClick={() => setShowResetHelp((prev) => !prev)}
                aria-expanded={showResetHelp}
              >
                Forgot password?
              </button>
            </div>

            {showResetHelp && (
              <div className="bo-login__note" role="status">
                <i className="fas fa-circle-info" aria-hidden></i>
                <span>
                  Password resets are handled by your ServeIQ administrator.
                  Contact them to have a new password issued for this account.
                </span>
              </div>
            )}

            <button type="submit" className="bo-login__submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="bo-login__spinner" aria-hidden></span>
                  Signing in&hellip;
                </>
              ) : (
                <>
                  Sign in
                  <i className="fas fa-arrow-right" aria-hidden></i>
                </>
              )}
            </button>
          </form>

          <p className="bo-login__card-foot">
            <i className="fas fa-shield-halved" aria-hidden></i>
            Secured with an encrypted session
          </p>
        </div>
      </main>

      <div className="login-bg-list clearfix" style={{ display: "none" }}>
        <div className={`login-bg-list-item ${bg1 ? 'active' : ''}`}>
          <Link to="/user/login-v2" onClick={(e) => selectBg(e, 'bg1', '/assets/img/login-bg/login-bg-17.jpg')} style={{ backgroundImage: 'url(/assets/img/login-bg/login-bg-17.jpg)' }} className="login-bg-list-link"></Link>
        </div>
        <div className={`login-bg-list-item ${bg2 ? 'active' : ''}`}>
          <Link to="/user/login-v2" onClick={(e) => selectBg(e, 'bg2', '/assets/img/login-bg/login-bg-16.jpg')} style={{ backgroundImage: 'url(/assets/img/login-bg/login-bg-16.jpg)' }} className="login-bg-list-link"></Link>
        </div>
        <div className={`login-bg-list-item ${bg3 ? 'active' : ''}`}>
          <Link to="/user/login-v2" onClick={(e) => selectBg(e, 'bg3', '/assets/img/login-bg/login-bg-15.jpg')} style={{ backgroundImage: 'url(/assets/img/login-bg/login-bg-15.jpg)' }} className="login-bg-list-link"></Link>
        </div>
        <div className={`login-bg-list-item ${bg4 ? 'active' : ''}`}>
          <Link to="/user/login-v2" onClick={(e) => selectBg(e, 'bg4', '/assets/img/login-bg/login-bg-14.jpg')} style={{ backgroundImage: 'url(/assets/img/login-bg/login-bg-14.jpg)' }} className="login-bg-list-link"></Link>
        </div>
        <div className={`login-bg-list-item ${bg5 ? 'active' : ''}`}>
          <Link to="/user/login-v2" onClick={(e) => selectBg(e, 'bg5', '/assets/img/login-bg/login-bg-13.jpg')} style={{ backgroundImage: 'url(/assets/img/login-bg/login-bg-13.jpg)' }} className="login-bg-list-link"></Link>
        </div>
        <div className={`login-bg-list-item ${bg6 ? 'active' : ''}`}>
          <Link to="/user/login-v2" onClick={(e) => selectBg(e, 'bg6', '/assets/img/login-bg/login-bg-12.jpg')} style={{ backgroundImage: 'url(/assets/img/login-bg/login-bg-12.jpg)' }} className="login-bg-list-link"></Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
